import React, { Fragment, useState } from 'react';
import { clone, isEmpty, pluck } from 'underscore';
import { useSelector } from 'react-redux';
import t from 'react-translate';

// redux
import { getCurrentCourse } from 'redux/selectors/course';
import { RootState } from 'redux/schemas';
import { getLecturePage } from 'redux/selectors/timeline';
import { getQuizQuestions,
  getQuizQuestionOptions,
  getSubmissionQuestionResponse,
} from 'redux/selectors/quizzes';
import { NAllQuestionViewQuiz } from 'redux/schemas/models/all-question-view-quiz';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';

import useSavingRegistry, { SavingRegistryContext } from 'shared/hooks/use-saving-registry';
import QuizQuestion from 'quizzes/components/quiz-question';
import QuizContent from 'quizzes/components/quiz-content';
import { NQuizQuestion, QuizQuestionType, ResponseOption } from 'redux/schemas/models/quiz';
import CorrectAnswer from 'quizzes/components/correct-answer';
import { css } from '@emotion/core';
import { gray5, gray6 } from 'styles/global_defaults/colors';
import { doubleSpacing } from 'styles/global_defaults/scaffolding';
import ProgressiveFeedback from 'quizzes/components/progressive-feedback';
import QuizContext,
{ SavingIndicator, QuestionContext } from '../../../quizzes/components/context';
import { LecturePageMode } from '..';

type AllQuestionViewQuestionProps = {
  currentQuestion: NQuizQuestion
  quiz: NAllQuestionViewQuiz;
  lectureComponent: NLectureComponent<ComponentType.QUIZ>;
  mode: LecturePageMode;
  showCorrectAnswers: boolean;
};

const AllQuestionViewQuestion = (props: AllQuestionViewQuestionProps) => {
  const {
    quiz,
    lectureComponent,
    mode,
    showCorrectAnswers,
  } = props;

  const styles = css`
    .question-container {
      border-bottom: 1px solid ${gray6};
      .number-container {
        width: ${doubleSpacing}px;
      }
      .question-content {
        width: calc(100% - ${doubleSpacing}px);
        .correct-answer-container {
          border-top: 4px solid ${gray5};
        }
      }
    }
  `;

  const questions: NQuizQuestion[] = useSelector(
    (state: RootState) => getQuizQuestions(state, quiz.id, true),
  );
  const ownerLecturePage = useSelector((state: RootState) => getLecturePage(state, lectureComponent.id));

  const [savingStatus, setSavingStatus] = useState(SavingIndicator.HIDDEN_STATUS);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isCurrentFeedbackShown, setIsCurrentFeedbackShown] = React.useState(false);

  const { currentQuestion } = props;
  const isQuestionUnAnswered = () => {
    switch (currentQuestion.type) {
      case QuizQuestionType.MULTIPLE_CHOICE:
      case QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION:
      case QuizQuestionType.NUMBER_QUESTION:
      case QuizQuestionType.LONG_ANSWER_QUESTION:
      case QuizQuestionType.LIKERT_SCALE_QUESTION:
        return !getCurrentQuestionResponse()?.response;
      case QuizQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER:
        return isEmpty(getCurrentQuestionResponse()?.response);
      case QuizQuestionType.TABLE_SHORT_ANSWER_QUESTION:
      case QuizQuestionType.TABLE_NUMBER_QUESTION:
      {
        // For table questions, childQuestionResponses need to be considered
        // to check whether the table question is unanswered.
        const answeredChildQuestionResponses = getCurrentQuestionResponse()
          .childrenQuestionResponses
          .filter((eachResponse) => {
            if (!eachResponse.readOnly) {
              return !!eachResponse?.response;
            }
            return false;
          });

        // if no response from answer input cells of table
        return answeredChildQuestionResponses.length === 0;
      }
      default:
        return false;
    }
  };

  const currentCourse = useSelector(getCurrentCourse);
  const canMakeStructuralChanges = currentCourse.isContentManagementCollection ? !quiz.submission : !ownerLecturePage?.released;
  const savingIndicatorTimeoutRef = React.useRef<null | NodeJS.Timeout>(null);
  const savingRegistry = useSavingRegistry();
  const responses = useSelector((state: RootState) => getSubmissionQuestionResponse(state, currentQuestion.id, currentQuestion.questionSetId));
  const isHardDeadlineExpired = quiz.hardDeadline && quiz.expired;
  const { isContentManagementCollection } = currentCourse;
  const isAtLeastOneBottomSectionElementShown = isCurrentFeedbackShown || showCorrectAnswers;

  const getCurrentQuestionResponse = () => {
    if (quiz.unsubmittedSubmission) {
      return null;
    }
    if (responses?.length) {
      const response = clone(responses[0]);
      response.feedback = pluck(response.feedback, 'id');
      return response;
    }
    return null;
  };

  const quizContextValue = {
    questions,
    setSavingStatus,
    currentQuestion,
    quiz,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    lectureComponent,
    canMakeStructuralChanges,
    savingIndicatorTimeoutRef,
    isAllQuestionViewQuiz: true,
    currentQuestionResponse: getCurrentQuestionResponse(),
    lecturePageMode: mode,
    isHardDeadlineExpired,
    isContentManagementCollection,
    showCorrectAnswers,
  };
  const responseOptions: ResponseOption[] = useSelector(
    (state: RootState) => getQuizQuestionOptions(state, currentQuestion?.id),
  );

  const questionContextValue = {
    currentQuestion,
    responseOptions,
    currentQuestionResponse: getCurrentQuestionResponse(),
  };

  const getFeedbackSection = () => {
    switch (currentQuestion.type) {
      case QuizQuestionType.MULTIPLE_CHOICE:
      case QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION:
      case QuizQuestionType.NUMBER_QUESTION:
      case QuizQuestionType.LIKERT_SCALE_QUESTION:
        if (isQuestionUnAnswered() || showCorrectAnswers) {
          return (
            <div className='correct-answer-container mb-5 mt-2'>
              {showCorrectAnswers && (<CorrectAnswer className='mt-1' />)}
              {isQuestionUnAnswered()
                && (
                  <p className='mb-0 mt-1 text-danger bold text-large-body'>
                    {t.QUIZZES.EMPTY_RESPONSE()}
                  </p>
                )}
            </div>
          );
        }
        return null;
      case QuizQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER:
        if (isQuestionUnAnswered() || showCorrectAnswers || getCurrentQuestionResponse()) {
          return (
            <Fragment>
              <div>
                {(!isQuestionUnAnswered())
                  && (
                    <ProgressiveFeedback onVisibleChange={setIsCurrentFeedbackShown} />
                  )}
              </div>
              {(showCorrectAnswers || isQuestionUnAnswered())
                  && (
                    <div className='correct-answer-container mb-5 mt-2'>
                      {showCorrectAnswers && (<CorrectAnswer className='mt-1' />)}
                      {isQuestionUnAnswered()
                        && (
                          <p className='mb-0 mt-1 text-danger bold text-large-body'>
                            {t.QUIZZES.EMPTY_RESPONSE()}
                          </p>
                        )}
                    </div>
                  )}
            </Fragment>
          );
        }
        return null;
      case QuizQuestionType.LONG_ANSWER_QUESTION:
        if (isQuestionUnAnswered() || showCorrectAnswers) {
          return (
            <div className='correct-answer-container mb-5 mt-2'>
              {showCorrectAnswers
                && (
                  <p className='text-large-body bold text-success mt-1 mb-0'>
                    {t.QUIZZES.CORRECT_ANSWER()}
                    {' '}
                    {t.QUIZZES.LONG_ANSWER_QUESTION.DEFAULT_FEEDBACK()}
                  </p>
                )}
              {isQuestionUnAnswered()
                && (
                  <p className='mb-0 mt-1 text-danger bold text-large-body'>
                    {t.QUIZZES.EMPTY_RESPONSE()}
                  </p>
                )}
            </div>
          );
        }
        return null;
      case QuizQuestionType.TABLE_SHORT_ANSWER_QUESTION:
      case QuizQuestionType.TABLE_NUMBER_QUESTION:
        if (showCorrectAnswers) {
          return (
            <div className='correct-answer-container mb-5 mt-2'>
              <p className='text-large-body bold text-success mt-1 mb-0'>
                {t.QUIZZES.CORRECT_ANSWER()}
                {' '}
                {getCurrentQuestionResponse().isCorrect
                  ? t.QUIZZES.TABLE_QUESTION.CORRECT_ANSWER_INFO()
                  : t.QUIZZES.TABLE_QUESTION.INCORRECT_ANSWER_INFO()}
              </p>
            </div>
          );
        }
        if (isQuestionUnAnswered() || !getCurrentQuestionResponse().isCorrect) {
          return (
            <div className='correct-answer-container mb-5 mt-2'>
              {isQuestionUnAnswered()
                && (
                  <p className='mb-0 mt-1 text-danger bold text-large-body'>
                    {t.QUIZZES.EMPTY_RESPONSE()}
                  </p>
                )}
              {/* If any of tabel answer cell is answered but table answer is incorrect */}
              {!getCurrentQuestionResponse().isCorrect
                && !isQuestionUnAnswered()
                && (
                  <p className='mb-0 mt-1 text-danger bold text-large-body'>
                    {t.QUIZZES.TABLE_QUESTION.INCORRECT_ANSWER_INFO()}
                  </p>
                )}
            </div>
          );
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <SavingRegistryContext.Provider value={savingRegistry}>
      <QuizContext.Provider value={quizContextValue}>
        <QuestionContext.Provider value={questionContextValue}>
          <div css={styles}>
            <div className='d-flex w-100 question-container pt-6 pb-2'>
              <div className='number-container text-large-regular bold'>
                {currentQuestion.displayIndex}
              </div>
              <div className='d-flex flex-column flex-grow-1 question-content'>
                <QuizQuestion currentQuestion={currentQuestion} />
                {(mode === LecturePageMode.VIEW) && !!getCurrentQuestionResponse() && (
                  getFeedbackSection()
                )}
              </div>
            </div>
          </div>
        </QuestionContext.Provider>
      </QuizContext.Provider>
    </SavingRegistryContext.Provider>
  );
};

export default AllQuestionViewQuestion;
